import React, { useState } from "react";
import { Modal, View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Checkbox } from "react-native-paper";
import ProjectRecordDocPicker from "./ProjectRecordDocPicker";
import ToastMessageAnnimated from "./Toast";

export default function ProjectRecordUploadModal({
  visible,
  onClose,
  onFileUploaded,
  clientAPIURL,
  parentId,
  listId,
  getBlobId,
  uploadEndpoint,
}: {
  visible: boolean;
  onClose: () => void;
  onFileUploaded: (response: string) => void;
  clientAPIURL: string;
  parentId: string;
  listId: string;
  getBlobId: (name: string) => string | undefined;
  uploadEndpoint: string;
}) {
  const [overwrite, setOverwrite] = useState<boolean>(false);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleModalClose = () => {
    if (isUploading) return;
    setOverwrite(false);
    setWarningVisible(false);
    setToastVisible(false);
    onClose();
  };

  const handleAllUploaded = () => {
    setToastVisible(true);
    setTimeout(() => {
      handleModalClose();
    }, 2000);
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={handleModalClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.headerRow}>
            <Text style={styles.modalTitle}>Upload Files</Text>
            <TouchableOpacity 
              onPress={handleModalClose} 
              style={[styles.closeButton, isUploading && { opacity: 0.3 }]} 
              disabled={isUploading}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>
          
          <Text style={styles.helperText}>
            Toggle "Overwrite" before selecting or dropping files if you wish to replace existing files.
          </Text>

          {/* Overwrite Checkbox */}
          <View style={[styles.checkboxContainer, isUploading && { opacity: 0.5 }]}>
            <Checkbox
              status={overwrite ? "checked" : "unchecked"}
              onPress={() => {
                if (!isUploading) setOverwrite(!overwrite);
              }}
              uncheckedColor="#666"
              color="#007BFF"
            />
            <Text style={styles.checkboxLabel}>Overwrite if file exists</Text>
          </View>

          {warningVisible && (
            <Text style={styles.warningText}>{warningMessage}</Text>
          )}

          <ProjectRecordDocPicker
            formMode="Edit"
            onFileUploaded={onFileUploaded}
            clientAPIURL={clientAPIURL}
            showWarning={setWarningVisible}
            warningText={setWarningMessage}
            parentId={parentId}
            listId={listId}
            overwrite={overwrite}
            getBlobId={getBlobId}
            uploadEndpoint={uploadEndpoint}
            onAllFilesUploaded={handleAllUploaded}
            onUploadStateChange={setIsUploading}
          />

          <ToastMessageAnnimated
            message="Success: All files uploaded successfully."
            visible={toastVisible}
            onDismiss={() => setToastVisible(false)}
            indicator="success"
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    width: "80%",
    maxWidth: 600,
    backgroundColor: "white",
    padding: 20,
    borderRadius: 10,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  closeButton: {
    padding: 5,
  },
  closeButtonText: {
    fontSize: 18,
    color: "#666",
    fontWeight: "bold",
  },
  helperText: {
    fontSize: 13,
    color: "#666",
    marginBottom: 15,
    fontStyle: "italic",
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 15,
    backgroundColor: "#f9f9f9",
    padding: 5,
    borderRadius: 5,
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#333",
  },
  warningText: {
    color: "#D32F2F",
    fontSize: 13,
    marginBottom: 10,
    fontWeight: "500",
  },
});
